import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Star, Droplets, Sparkles, Wand2, Infinity as InfinityIcon, ArrowRight } from "lucide-react";
import { NewUserOfferStrip } from "@/components/billing/new-user-offer-strip";
import { useNewUserOffer } from "@/hooks/use-new-user-offer";

interface CreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  creditCost: number;
  credits: number;
}

const PERKS = [
  { Icon: Droplets, title: "Remove watermarks", desc: "Clean, share-ready downloads" },
  { Icon: Sparkles, title: "HD video & image quality", desc: "Sharper, higher-res results" },
  { Icon: Wand2, title: "All creation tools", desc: "Images, edits & videos" },
  { Icon: InfinityIcon, title: "Stars never expire", desc: "Use them whenever you like" },
];

/**
 * Out-of-credits moment. Shows what a pack unlocks and sends the user to the
 * billing page to choose one. Conversion analytics (Add to Cart) fires on the
 * billing page itself, not here.
 */
export function CreditsModal({ isOpen, onClose, creditCost, credits }: CreditsModalProps) {
  const { isActive: offerActive, countdownLabel } = useNewUserOffer({ startTimer: isOpen });

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-md" onClick={onClose} style={{ zIndex: 0 }} />
      <div
        className="relative rounded-3xl max-w-sm w-full mx-4 overflow-hidden shadow-2xl shadow-black/15 border border-white/40 backdrop-blur-xl animate-modal-in"
        style={{ zIndex: 1, background: "linear-gradient(170deg, #fff 0%, #faf6ed 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative px-6 pt-7 pb-4 text-center">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[100px] bg-orange-500/8 rounded-full blur-[60px] pointer-events-none" />
          <div className="relative">
            <div className="size-14 rounded-2xl bg-orange-50 border border-orange-200/60 flex items-center justify-center mx-auto mb-3">
              <Star className="size-7 text-[#e8822e]" />
            </div>
            <h3 className="text-xl font-extrabold text-foreground mb-1">You're out of stars</h3>
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              This costs <strong className="text-foreground">{creditCost} stars</strong> and you have{" "}
              <strong className="text-foreground">{credits}</strong>. Grab a pack to keep creating.
            </p>
          </div>
        </div>

        <NewUserOfferStrip isActive={offerActive} countdownLabel={countdownLabel} />

        <div className="px-5 pb-5 flex flex-col gap-2.5">
          {PERKS.map(({ Icon, title, desc }) => (
            <div key={title} className="flex items-center gap-3 rounded-2xl border border-[rgba(45,49,39,0.1)] bg-white/60 px-3.5 py-2.5">
              <div className="size-9 shrink-0 rounded-xl bg-orange-50 border border-orange-200/60 flex items-center justify-center">
                <Icon className="size-[18px] text-[#e8822e]" strokeWidth={2.4} />
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-extrabold text-[#2d3127] leading-tight">{title}</p>
                <p className="text-[11px] text-[#8a8570] leading-tight">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 pb-6 flex flex-col gap-2.5">
          <Link
            to="/billing"
            onClick={onClose}
            className={
              offerActive
                ? "w-full py-3.5 rounded-2xl bg-emerald-500 text-white text-[15px] font-extrabold shadow-lg shadow-emerald-500/25 hover:bg-emerald-600 hover:-translate-y-px transition-all flex items-center justify-center gap-2"
                : "w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#f0923a] to-[#e8822e] text-white text-[15px] font-extrabold shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-px transition-all flex items-center justify-center gap-2"
            }
          >
            {offerActive ? "Claim bonus packs" : "View Packs"}
            <ArrowRight className="size-4" />
          </Link>
          <button
            onClick={onClose}
            className="w-full py-2 rounded-xl text-[12.5px] font-semibold text-[#8a8570] hover:text-[#2d3127] transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
